import React, { useCallback, useEffect, useState } from 'react';
import CampaignsSection from './components/CampaignsSection';
import DocsSection from './components/DocsSection';
import SidebarAreaSwitcher from './components/SidebarAreaSwitcher';
import SidebarHeader from './components/SidebarHeader';
import SidebarPopups from './components/SidebarPopups';
import SidebarHistorySection from './components/SidebarHistorySection';
import SidebarUserFooter from './components/SidebarUserFooter';
import { useDocs } from '../../hooks/useDocs';
import { 
  useSidebarResize, 
  useSidebarData, 
  useSectionState, 
  usePopupManager,
  useCampaignDragState,
  useSavedViews,
  useSidebarMissionDrop,
} from './hooks';
import { BrainIcon, SearchIcon, TrashIcon } from './icons';
import {
  findUnlinkedNestedCampaigns,
  getUnverifiedNestParentIds,
} from '../../utils/nestedCampaignInvariant';

// Styles
import './styles/layout.css';
import './styles/sections.css';
import './styles/items.css';
import './styles/context-menu.css';
import './styles/search-popup.css';
// Note: unified-search.css styles are now imported by the UnifiedSearchPopup component itself

/**
 * Sidebar Component
 *
 * Structure:
 * - Header (brand, Brain, back/forward, collapse)
 * - Workspace switcher + Search (global)
 * - Area switcher: Workspace | Docs
 * - Area body (campaigns tree OR docs tree)
 * - Footer: Trash (campaigns + docs) + History + account
 *   (Settings lives in the navbar, left of the window controls)
 */
const Sidebar = ({ 
  sprints = [],
  selectedSprintId,
  selectedTemplateId,
  onSelectSprint,
  onSelectTemplate,
  onCreateSprint,
  onCreateNestedSprint,
  onCreateTemplate,
  onRenameSprint,
  onSprintIconChange,
  onDeleteSprint,
  onRestoreSprint,
  onPermanentlyDeleteSprint,
  onEmptyTrash,
  // Template-specific handlers (for UI differentiation, but use same underlying logic)
  onRenameTemplate,
  onDeleteTemplate,
  onRestoreTemplate,
  onPermanentlyDeleteTemplate,
  onDuplicateTemplate,
  onUseTemplate,
  onTemplateIconChange,
  onCreateTemplateFromCampaign,
  onNestCampaign,
  onReorderNestedCampaign,
  isCollapsed = false,
  appName = 'Fokus',
  onOpenDocs,
  onOpenWorkspaceArea,
  onOpenDashboards,
  sidebarArea: sidebarAreaProp,
  sprintOrder = [],
  onReorderSprints,
  activeView = null,
  onSelectView: onSelectViewProp,
  onOpenInNewTab,
  // User account props (moved from navbar)
  user,
  onLogout,
  workspaceId,
  workspaces = [],
  setWorkspace,
  onOpenSettings,
  deferOrphanDetection = false,
  loadFullSprint,
  // Brain (global agent — not a content area)
  brainOpen = false,
  onToggleBrain,
}) => {
  // ═══════════════════════════════════════════════════════════════
  // HOOKS
  // ═══════════════════════════════════════════════════════════════

  const { sidebarWidth, isResizing, sidebarRef, handleResizeStart, handleResizeReset } = useSidebarResize(isCollapsed);
  const { trashedDocs, restoreDoc, permanentlyDeleteDoc, docs, loading: docsLoading, createDoc, deleteDoc } = useDocs();
  const [sidebarArea, setSidebarArea] = useState(sidebarAreaProp || 'workspace');
  const [selectedDocId, setSelectedDocId] = useState(null);

  useEffect(() => {
    if (
      sidebarAreaProp === 'workspace' ||
      sidebarAreaProp === 'docs' ||
      sidebarAreaProp === 'dashboards'
    ) {
      setSidebarArea(sidebarAreaProp);
    }
  }, [sidebarAreaProp]);

  // Empty Trash covers docs as well as campaigns/templates
  const handleEmptyTrashWithDocs = useCallback(async () => {
    onEmptyTrash?.();
    await Promise.allSettled(trashedDocs.map((d) => permanentlyDeleteDoc(d.id)));
  }, [onEmptyTrash, trashedDocs, permanentlyDeleteDoc]);
  
  // Unified data model: sprints contains both campaigns and templates
  const {
    topLevelSprints, topLevelTemplates, nestedSprints, nestedInTemplates, 
    trashedSprints, allTrashedItems, orphanedNestedSprints, orphanedDetails,
    allActiveCampaigns, getTrashedItemInfo, activeSprintCount, templateCount, trashCount,
  } = useSidebarData(sprints, sprintOrder, { deferOrphanDetection, trashedDocs });

  const {
    campaignsExpanded, templatesExpanded, viewsExpanded, expandedSprints,
    handleToggleExpand, handleCollapseAllCampaignTrees, handleSelectSprint,
    handleToggleCampaigns, handleToggleTemplates, handleToggleViews,
  } = useSectionState(sprints, onSelectSprint, selectedSprintId);

  const {
    savedViews, selectedViewId, viewModalOpen, editingView,
    handleSelectView, handleRenameView, handleViewIconChange, handleDeleteView, handleDuplicateView,
    handleCaptureView, handleEditView, handleSaveView, handleCloseViewModal,
  } = useSavedViews({ activeView, onSelectView: onSelectViewProp, selectedSprintId });

  const {
    unifiedSearchOpen, trashPopupOpen, popupPosition,
    unifiedSearchBtnRef, trashBtnRef,
    openUnifiedSearch, openTrashPopup,
    closeUnifiedSearch, closeTrashPopup,
  } = usePopupManager();

  const {
    draggedSprintId, handleDragStart, handleDragEnd, handleReorderCampaign,
  } = useCampaignDragState(topLevelSprints, orphanedNestedSprints, orphanedDetails, onReorderSprints);

  const { missionDropTargetSprintId } = useSidebarMissionDrop();

  const hiddenNestRows = findUnlinkedNestedCampaigns(sprints);
  const unverifiedParentCount = getUnverifiedNestParentIds(sprints).length;
  const [scanningHiddenNests, setScanningHiddenNests] = useState(false);
  const [hiddenNestScanProgress, setHiddenNestScanProgress] = useState(null);
  const [puttingBackHiddenNestId, setPuttingBackHiddenNestId] = useState(null);

  const handleFindHiddenNests = useCallback(async () => {
    if (!loadFullSprint || scanningHiddenNests) return;
    const ids = getUnverifiedNestParentIds(sprints);
    if (ids.length === 0) return;
    setScanningHiddenNests(true);
    setHiddenNestScanProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i += 1) {
      try {
        await loadFullSprint(ids[i]);
      } catch {
        /* parent may 500; keep going so the rest can list */
      }
      setHiddenNestScanProgress({ done: i + 1, total: ids.length });
    }
    setScanningHiddenNests(false);
    setHiddenNestScanProgress(null);
  }, [loadFullSprint, scanningHiddenNests, sprints]);

  const handlePutBackHiddenNest = useCallback(async (row) => {
    const sprintId = row?.sprint?.id;
    const parentId = row?.parent?.id;
    if (!sprintId || !parentId || !onNestCampaign || puttingBackHiddenNestId) return;
    setPuttingBackHiddenNestId(sprintId);
    try {
      await onNestCampaign(sprintId, parentId, row.sprint?.name);
    } finally {
      setPuttingBackHiddenNestId(null);
    }
  }, [onNestCampaign, puttingBackHiddenNestId]);

  const handleTrashHiddenNest = useCallback((sprintId) => {
    onDeleteSprint?.(sprintId, { preserveLinkedMission: true });
  }, [onDeleteSprint]);

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleCreateCampaignFromSwitcher = useCallback(() => {
    setSidebarArea('workspace');
    onOpenWorkspaceArea?.();
    onCreateSprint?.();
  }, [onOpenWorkspaceArea, onCreateSprint]);

  const handleAreaChange = useCallback((area) => {
    setSidebarArea(area);
    if (area === 'docs') {
      onOpenDocs?.();
    } else if (area === 'dashboards') {
      onOpenDashboards?.();
    } else {
      onOpenWorkspaceArea?.();
    }
  }, [onOpenDocs, onOpenDashboards, onOpenWorkspaceArea]);

  const handleUnifiedSelectCampaign = useCallback((campaign, view) => {
    setSidebarArea('workspace');
    handleSelectSprint(campaign, { view });
  }, [handleSelectSprint]);

  const handleUnifiedSelectMission = useCallback((mission, campaign, view) => {
    setSidebarArea('workspace');
    handleSelectSprint(campaign, { view, missionId: mission.id });
  }, [handleSelectSprint]);

  const handleUnifiedSelectDoc = useCallback(
    (doc) => {
      setSidebarArea('docs');
      setSelectedDocId(doc.id);
      onOpenDocs?.(doc.id);
    },
    [onOpenDocs]
  );

  const handleSelectDoc = useCallback(
    (docId) => {
      setSelectedDocId(docId);
      onOpenDocs?.(docId);
    },
    [onOpenDocs]
  );

  const handleCreateDoc = useCallback(async () => {
    const doc = await createDoc({ title: 'Untitled Doc' });
    if (doc?.id) {
      setSelectedDocId(doc.id);
      onOpenDocs?.(doc.id);
    }
  }, [createDoc, onOpenDocs]);

  const handleDeleteDoc = useCallback(
    (docId) => {
      setSelectedDocId((current) => (current === docId ? null : current));
      deleteDoc(docId);
    },
    [deleteDoc]
  );

  // Selecting a campaign from the workspace tree leaves Docs area
  const handleSelectSprintFromTree = useCallback((sprint, options) => {
    setSidebarArea('workspace');
    handleSelectSprint(sprint, options);
  }, [handleSelectSprint]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <>
    <aside 
      ref={sidebarRef}
      className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : ''} ${isResizing ? 'sidebar--resizing' : ''}`}
      style={!isCollapsed ? { width: sidebarWidth } : undefined}
      aria-hidden={isCollapsed}
    >
      <SidebarHeader
        appName={appName}
        workspaceId={workspaceId}
        workspaces={workspaces}
        setWorkspace={setWorkspace}
        onOpenSettings={onOpenSettings}
      />

      <nav className="sidebar__nav">
        <div className="sidebar__nav-fixed">
          <button
            ref={unifiedSearchBtnRef}
            type="button"
            className="sidebar__search-btn"
            onClick={openUnifiedSearch}
            title="Search campaigns, missions & docs"
          >
            <SearchIcon size={13} />
            <span>Search</span>
          </button>
          <SidebarAreaSwitcher
            area={sidebarArea}
            onChange={handleAreaChange}
          />
        </div>

        <div className="sidebar__nav-scroll">
        {sidebarArea === 'docs' ? (
          <DocsSection
            docs={docs}
            loading={docsLoading}
            selectedDocId={selectedDocId}
            onSelectDoc={handleSelectDoc}
            onOpenInNewTab={onOpenInNewTab}
            onCreateDoc={handleCreateDoc}
            onDeleteDoc={handleDeleteDoc}
            createDoc={createDoc}
          />
        ) : sidebarArea === 'dashboards' ? (
          <p className="sidebar__docs-stub-text">
            Saved dashboards will show up here.
          </p>
        ) : (
          <CampaignsSection
            // Section state
            campaignsExpanded={campaignsExpanded}
            templatesExpanded={templatesExpanded}
            viewsExpanded={viewsExpanded}
            onToggleCampaigns={handleToggleCampaigns}
            onToggleTemplates={handleToggleTemplates}
            onToggleViews={handleToggleViews}
            // Campaigns
            topLevelSprints={topLevelSprints}
            nestedSprints={nestedSprints}
            allSprints={sprints}
            selectedSprintId={selectedSprintId}
            expandedSprints={expandedSprints}
            activeSprintCount={activeSprintCount}
            onSelectSprint={handleSelectSprintFromTree}
            onRenameSprint={onRenameSprint}
            onSprintIconChange={onSprintIconChange}
            onDeleteSprint={onDeleteSprint}
            onNestCampaign={onNestCampaign}
            onReorderCampaign={handleReorderCampaign}
            onReorderNestedCampaign={onReorderNestedCampaign}
            onCreateNestedSprint={onCreateNestedSprint}
            onToggleExpand={handleToggleExpand}
            onCreateCampaign={handleCreateCampaignFromSwitcher}
            onCollapseAllCampaigns={handleCollapseAllCampaignTrees}
            canCollapseAll={expandedSprints.size > 0}
            // Drag state
            draggedSprintId={draggedSprintId}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            missionDropTargetSprintId={missionDropTargetSprintId}
            // Templates (from unified data)
            templates={topLevelTemplates}
            nestedInTemplates={nestedInTemplates}
            selectedTemplateId={selectedTemplateId}
            templateCount={templateCount}
            onSelectTemplate={onSelectTemplate}
            onRenameTemplate={onRenameTemplate}
            onTemplateIconChange={onTemplateIconChange}
            onDeleteTemplate={onDeleteTemplate}
            onDuplicateTemplate={onDuplicateTemplate}
            onUseTemplate={onUseTemplate}
            onCreateTemplate={onCreateTemplate}
            onCreateTemplateFromCampaign={onCreateTemplateFromCampaign}
            // Views
            savedViews={savedViews}
            selectedViewId={selectedViewId}
            onSelectView={handleSelectView}
            onRenameView={handleRenameView}
            onViewIconChange={handleViewIconChange}
            onDeleteView={handleDeleteView}
            onDuplicateView={handleDuplicateView}
            onEditView={handleEditView}
            onCaptureView={handleCaptureView}
            // Open in new tab
            onOpenInNewTab={onOpenInNewTab}
          />
        )}

        </div>
      </nav>

      {/* Footer — Trash (always; campaigns + docs) + History + Account */}
      <div className="sidebar-bottom">
        <div className="sidebar-bottom__utils">
          <button
            ref={trashBtnRef}
            type="button"
            className={`sidebar-history__btn sidebar-bottom__trash ${trashCount > 0 ? 'sidebar-bottom__trash--has-items' : ''}`}
            onClick={openTrashPopup}
            title="Trash"
          >
            <TrashIcon size={14} />
            {trashCount > 0 && (
              <span className="sidebar-history__count">{trashCount}</span>
            )}
          </button>
          <SidebarHistorySection />
        </div>
        <div className="sidebar-bottom__end">
          {onToggleBrain && (
            <button
              type="button"
              className={`sidebar__brain-toggle ${brainOpen ? 'sidebar__brain-toggle--active' : ''}`}
              onClick={onToggleBrain}
              title={brainOpen ? 'Close Brain' : 'Open Brain'}
              aria-label={brainOpen ? 'Close Brain' : 'Open Brain'}
              aria-pressed={brainOpen}
            >
              <BrainIcon size={15} />
            </button>
          )}
          <SidebarUserFooter user={user} onLogout={onLogout} />
        </div>
      </div>

      {/* Popups & Modals */}
      <SidebarPopups
        // Unified search
        unifiedSearchOpen={unifiedSearchOpen}
        closeUnifiedSearch={closeUnifiedSearch}
        allActiveCampaigns={allActiveCampaigns}
        allSprints={sprints}
        docs={docs}
        onSelectCampaign={handleUnifiedSelectCampaign}
        onSelectMission={handleUnifiedSelectMission}
        onSelectDoc={handleUnifiedSelectDoc}
        onCreateCampaign={onCreateSprint}
        // Trash (unified: campaigns + templates)
        trashPopupOpen={trashPopupOpen}
        closeTrashPopup={closeTrashPopup}
        allTrashedItems={allTrashedItems}
        trashCount={trashCount}
        getTrashedItemInfo={getTrashedItemInfo}
        onRestoreSprint={onRestoreSprint}
        onPermanentlyDeleteSprint={onPermanentlyDeleteSprint}
        onRestoreTemplate={onRestoreTemplate}
        onPermanentlyDeleteTemplate={onPermanentlyDeleteTemplate}
        onRestoreDoc={restoreDoc}
        onPermanentlyDeleteDoc={permanentlyDeleteDoc}
        onEmptyTrash={handleEmptyTrashWithDocs}
        hiddenNestRows={hiddenNestRows}
        unverifiedParentCount={unverifiedParentCount}
        scanningHiddenNests={scanningHiddenNests}
        hiddenNestScanProgress={hiddenNestScanProgress}
        onFindHiddenNests={handleFindHiddenNests}
        puttingBackHiddenNestId={puttingBackHiddenNestId}
        onPutBackHiddenNest={handlePutBackHiddenNest}
        onTrashHiddenNest={handleTrashHiddenNest}
        // View modal
        viewModalOpen={viewModalOpen}
        closeViewModal={handleCloseViewModal}
        onSaveView={handleSaveView}
        editingView={editingView}
        // Shared
        popupPosition={popupPosition}
      />
    </aside>

    {/* Resize handle — sibling of the sidebar (not a child) because the
        sidebar's `contain: paint` clips children to its box, which would
        make a handle hanging into the gutter unclickable. */}
    {!isCollapsed && (
      <div
        className={`sidebar__resize-handle ${isResizing ? 'sidebar__resize-handle--active' : ''}`}
        onMouseDown={handleResizeStart}
        onDoubleClick={handleResizeReset}
        title="Drag to resize • Double-click to reset"
      />
    )}
    </>
  );
};

export default Sidebar;
